/* Pro-only "Orbit Header" — avatar in centre, user's actual social icons orbiting around it.
   Used at the top of /u/:username for Pro users who enabled it.
   Respects orbit_speed, orbit_direction, orbit_platforms (subset filter) and orbit_spotlight_color. */
import React, { useEffect, useRef } from 'react';
import { motion, useMotionValue, useSpring, animate, useReducedMotion } from 'framer-motion';
import { socialPlatforms } from '../utils/socialPlatforms';
import { getOrbitSpeed, getAvatarShape } from '../utils/customisation';
import { getReadableIconColor } from '../utils/iconContrast';

const RADII = [1.10, 1.18, 1.10, 1.18, 1.10, 1.18, 1.10, 1.18, 1.10, 1.18, 1.10, 1.18];

const OrbitHeader = ({ profile, profilePicUrl, ts }) => {
  const reduce = useReducedMotion();
  const orbitBoxRef = useRef(null);

  const speed = getOrbitSpeed(profile.orbit_speed);
  const shape = getAvatarShape(profile.avatar_shape);
  const avatarShapeStyle = {
    borderRadius: shape.borderRadius,
    ...(shape.clipPath ? { clipPath: shape.clipPath } : {}),
  };
  const direction = profile.orbit_direction === 'ccw' ? -1 : 1;
  const spotlightColor = profile.orbit_spotlight_color || ts['--theme-accent'] || '#0055FF';

  // Build the icon list: filter user's actual links by orbit_platforms (if set), else use all unique platforms.
  const allLinks = (profile.links || []).filter(l => l.platform && l.url);
  const platformFilter = (profile.orbit_platforms || []).filter(Boolean);
  const seen = new Set();
  let icons = [];
  for (const link of allLinks) {
    if (seen.has(link.platform)) continue;
    if (platformFilter.length > 0 && !platformFilter.includes(link.platform)) continue;
    const platform = socialPlatforms.find(p => p.id === link.platform);
    if (!platform) continue;
    seen.add(link.platform);
    icons.push({ Icon: platform.icon, color: link.color || platform.color, id: link.platform, url: link.url });
    if (icons.length >= 12) break;
  }

  // Continuous rotation
  const orbitAngle = useMotionValue(0);
  const counterRot = useMotionValue(0);
  useEffect(() => {
    if (reduce || icons.length === 0) return;
    const controls = animate(orbitAngle, 360 * direction, {
      duration: speed.seconds,
      ease: 'linear',
      repeat: Infinity,
    });
    const counterControls = animate(counterRot, -360 * direction, {
      duration: speed.seconds,
      ease: 'linear',
      repeat: Infinity,
    });
    return () => { controls.stop(); counterControls.stop(); };
  }, [reduce, icons.length, direction, speed.seconds, orbitAngle, counterRot]);

  // Mouse-tracking head tilt (subtle, body-level)
  const tiltX = useMotionValue(0);
  const tiltY = useMotionValue(0);
  const springX = useSpring(tiltX, { stiffness: 80, damping: 18, mass: 0.6 });
  const springY = useSpring(tiltY, { stiffness: 80, damping: 18, mass: 0.6 });
  useEffect(() => {
    if (reduce) return;
    const handleMove = (e) => {
      const box = orbitBoxRef.current;
      if (!box) return;
      const rect = box.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = (e.clientX - cx) / (window.innerWidth / 2);
      const dy = (e.clientY - cy) / (window.innerHeight / 2);
      tiltX.set(Math.max(-1, Math.min(1, dx)) * 10);
      tiltY.set(Math.max(-1, Math.min(1, -dy)) * 8);
    };
    window.addEventListener('mousemove', handleMove);
    return () => window.removeEventListener('mousemove', handleMove);
  }, [reduce, tiltX, tiltY]);

  if (icons.length < 3) return null; // not enough links to make an orbit meaningful

  return (
    <div
      ref={orbitBoxRef}
      className="relative mx-auto w-full max-w-[360px] aspect-square mb-6"
      data-testid="orbit-header"
    >
      {/* Faint orbit reference rings */}
      <div className="absolute inset-[10%] rounded-full border" style={{ borderColor: `${ts['--theme-card-border']}` }} />
      <div className="absolute inset-[20%] rounded-full border" style={{ borderColor: `${ts['--theme-card-border']}`, opacity: 0.6 }} />

      {/* Spotlight glow */}
      <div
        className="absolute w-[60%] aspect-square rounded-full blur-3xl pointer-events-none"
        style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)', background: spotlightColor, opacity: 0.35 }}
      />

      {/* Centered avatar — outer wrapper handles positioning so it isn't clobbered
         by Framer Motion's inline transform (scale + rotate). */}
      <div
        className="absolute w-[42%] aspect-square z-20"
        style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}
      >
        <motion.div
          className="w-full h-full"
          style={{ perspective: 800 }}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <motion.div
            style={{
              rotateY: reduce ? 0 : springX,
              rotateX: reduce ? 0 : springY,
              transformStyle: 'preserve-3d',
            }}
            className="w-full h-full"
          >
            {profilePicUrl ? (
              <img
                src={profilePicUrl}
                alt={profile.name}
                className="w-full h-full object-cover select-none"
                style={{
                  border: `3px solid ${ts['--theme-card-border']}`,
                  boxShadow: `0 30px 60px ${spotlightColor}55`,
                  ...avatarShapeStyle,
                }}
                draggable={false}
              />
            ) : (
              <div
                className="w-full h-full flex items-center justify-center text-5xl font-bold"
                style={{
                  background: spotlightColor,
                  color: ts['--theme-accent-text'],
                  border: `3px solid ${ts['--theme-card-border']}`,
                  boxShadow: `0 30px 60px ${spotlightColor}55`,
                  ...avatarShapeStyle,
                }}
              >
                {profile.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
            )}
          </motion.div>
        </motion.div>
      </div>

      {/* Orbiting icon group */}
      <motion.div className="absolute inset-0 z-30" style={{ rotate: orbitAngle }}>
        {icons.map((cfg, i) => {
          const angle = (360 / icons.length) * i - 90; // start at top
          const radius = RADII[i % RADII.length];
          const rad = (angle * Math.PI) / 180;
          const left = 50 + Math.cos(rad) * 38 * radius;
          const top = 50 + Math.sin(rad) * 38 * radius;
          const { Icon, color, id, url } = cfg;
          return (
            <div
              key={`${id}-${i}`}
              className="absolute"
              style={{ left: `${left}%`, top: `${top}%`, transform: 'translate(-50%, -50%)' }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.3 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.3 + i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                style={{ rotate: counterRot }}
              >
                <motion.a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.12 }}
                  whileTap={{ scale: 0.95 }}
                  className="relative block w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center backdrop-blur-xl border"
                  style={{
                    borderColor: `${color}40`,
                    background: `${color}15`,
                    boxShadow: `0 8px 24px ${color}55, inset 0 0 0 1px ${color}30`,
                  }}
                  data-testid={`orbit-icon-${id}`}
                >
                  <Icon className="w-5 h-5" style={{ color: getReadableIconColor(color, ts['--theme-bg']) }} />
                  <span
                    className="absolute -inset-2 -z-10 rounded-full blur-2xl opacity-60"
                    style={{ background: color }}
                  />
                </motion.a>
              </motion.div>
            </div>
          );
        })}
      </motion.div>
    </div>
  );
};

export default OrbitHeader;
